import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import {
  listOrganizationMCPServers,
  listUserMCPServers,
  getMCPServerUsers,
  type ListMCPServersParams,
} from '@kirin/api-client/api/mcp';
import type { MCPServerUsersResponse } from '@kirin/api-client/contracts/mcp';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from './queryKeys';
import { cacheConfigForCollaborativeData } from './queryConfig';
import { retryHandlers } from './helpers';

/**
 * Hook to fetch organization MCP servers with pagination and filtering
 *
 * Authorization:
 * - Owner/Manager: Returns all MCP servers in the organization
 * - Member: Returns only the user's own MCP servers
 *
 * @param organizationId - Organization ID (required)
 * @param params - Query parameters for filtering and pagination
 * @param enabled - Whether to enable the query
 *
 * @example
 * const { data, isLoading, error } = useMCPServers(orgId, { page: 1, limit: 50 });
 *
 * @returns React Query result with MCP servers list
 */
export function useMCPServers(
  organizationId: string | undefined,
  params: ListMCPServersParams = {},
  enabled: boolean = true
) {
  return useQuery({
    queryKey: queryKeys.mcp.list(organizationId, params),
    queryFn: () => {
      if (!organizationId) {
        throw new Error('organizationId is required');
      }
      return listOrganizationMCPServers(apiClient, organizationId, params);
    },
    enabled: enabled && !!organizationId,
    ...cacheConfigForCollaborativeData,
    retry: retryHandlers.list,
  });
}

/**
 * Hook to fetch personal MCP servers (for private users without an organization)
 *
 * Returns MCP servers where organizationId is NULL, filtered by the authenticated user.
 *
 * @param params - Query parameters for filtering and pagination
 * @param enabled - Whether to enable the query
 *
 * @example
 * const { data, isLoading, error } = useUserMCPServers({ page: 1, limit: 50 });
 *
 * @returns React Query result with personal MCP servers list
 */
export function useUserMCPServers(
  params: ListMCPServersParams = {},
  enabled: boolean = true
) {
  return useQuery({
    queryKey: queryKeys.mcp.personal(params),
    queryFn: () => listUserMCPServers(apiClient, params),
    enabled,
    ...cacheConfigForCollaborativeData,
    retry: retryHandlers.list,
  });
}

/**
 * Hook to fetch users who have a specific MCP server configured
 *
 * Authorization:
 * - Owner/Manager: Returns all users with this MCP
 * - Member: Returns only the requesting user's data
 *
 * @param organizationId - Organization ID (required)
 * @param serverName - MCP server name (required)
 * @param enabled - Whether to enable the query
 *
 * @example
 * const { data, isLoading, error } = useMCPServerUsers(orgId, 'filesystem');
 *
 * @returns React Query result with users list
 */
export function useMCPServerUsers<TSelected = MCPServerUsersResponse>(
  organizationId: string | undefined,
  serverName: string | undefined,
  enabled: boolean = true,
  options?: Omit<
    UseQueryOptions<MCPServerUsersResponse, unknown, TSelected>,
    'queryKey' | 'queryFn' | 'enabled' | 'retry'
  >
) {
  return useQuery<MCPServerUsersResponse, unknown, TSelected>({
    queryKey: queryKeys.mcp.users(organizationId, serverName),
    queryFn: () => {
      if (!organizationId || !serverName) {
        throw new Error('organizationId and serverName are required');
      }
      return getMCPServerUsers(apiClient, organizationId, serverName);
    },
    enabled: enabled && !!organizationId && !!serverName,
    ...cacheConfigForCollaborativeData,
    retry: retryHandlers.detail,
    ...options,
  });
}
